/**
 * 날짜/기간 값의 **표시 규약** 단일 정본 — 순수 함수만(React 없음).
 *
 * 같은 날짜 값이 여러 surface(작성폼 트리 위젯 · 행 그리드 · 표 셀 바인딩 · 요약 헤더)에서
 * 문자열로 접히는데, 그 규칙이 자리마다 갈라지면 같은 값이 화면마다 다르게 보인다. 그래서
 * "값 → 보이는 글자"는 전부 여기를 지난다. 컴포넌트(DateFieldInput)는 이 모듈을 쓰고,
 * 이 모듈은 컴포넌트를 모른다 — 순수 로직이라 단위 테스트 대상이고, React를 끌어오지 않아
 * 순수 모듈(dataSourceCollection 등)에서도 그대로 쓸 수 있다.
 *
 * 값 채널(저장되는 값)은 두 가지뿐이다:
 *  - date       : `yyyy-MM-dd` 평문 문자열
 *  - date-range : `{ start?, end? }` 객체(각 `yyyy-MM-dd`) — 한쪽만 채워진 상태가 정상
 */
import type { DateRangeValue } from '@safetyhub/shared';

/**
 * date input(value 규격 yyyy-MM-dd) 표시값 정규화 — prefill 등이 ISO datetime(2026-05-19T06:11:57Z)을
 * 넣으면 브라우저가 무시해 빈 칸으로 보이므로, 로컬 날짜(yyyy-MM-dd)로 변환해 표시한다(시간대 정책:
 * 표시=로컬). 이미 yyyy-MM-dd거나 파싱 불가면 그대로/빈값. 저장값은 onChange(yyyy-MM-dd)에서 갱신.
 */
export function dateInputValue(value: unknown): string {
  if (typeof value !== 'string' || value === '') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * 날짜 표시 기본 pattern — resolver `applyFormat({kind:'date'})`의 `pattern` 미지정 기본값이자
 * `dateDisplayLabel`이 내는 형태("2026년 7월 21일", 월·일 0 미패딩)의 정본. 두 자리가 같은 표기를
 * 각자 적고 있었다(2026-08-30 리뷰 INFO) — 구현은 합치지 않는다: `dateDisplayLabel`은 yyyy-MM-dd
 * 문자열을 분해해 시간대 무관이고, resolver `formatDate`는 Date 파싱(ISO datetime·Date 인스턴스)이라
 * 입력 계약이 다르다. 표기만 이 한 곳에서 맞춘다.
 */
export const DATE_DISPLAY_PATTERN_DEFAULT = 'YYYY년 M월 D일';

/** date 위젯 표시 라벨 — yyyy-MM-dd를 `DATE_DISPLAY_PATTERN_DEFAULT` 형태로. 값 없음/파싱 불가면 ''. */
export function dateDisplayLabel(value: unknown): string {
  const normalized = dateInputValue(value);
  if (normalized === '') return '';
  const [y, m, d] = normalized.split('-');
  return `${Number(y)}년 ${Number(m)}월 ${Number(d)}일`;
}

export const DATE_PLACEHOLDER_DEFAULT = '날짜를 선택해 주세요.';

/** 기간 컨트롤의 시작/종료 칸 기본 안내 문구 — 어느 칸인지 구분돼야 두 칸이 헷갈리지 않는다. */
export const DATE_RANGE_START_PLACEHOLDER_DEFAULT = '시작일';
export const DATE_RANGE_END_PLACEHOLDER_DEFAULT = '종료일';

/** 기간 표시의 구분자 — 표시/편집/셀이 같은 글자를 쓰도록 한 곳에 둔다. */
export const DATE_RANGE_SEPARATOR = '~';

/**
 * 값이 기간 객체(`{ start?, end? }`)인지 — **문자열 전제 경로의 방어 판정**이다.
 *
 * `isAnnotatedImageValue`와 같은 성질: 값이 객체인 채로 `String()`을 타면 `[object Object]`가
 * 그대로 화면에 나가므로, 텍스트로 접는 자리마다 이 판정으로 먼저 갈라야 한다. 두 키 중 하나라도
 * 문자열이면 기간으로 본다(한쪽만 채워진 상태가 정상이므로) — 빈 객체 `{}`도 기간으로 인정한다
 * (행 추가 직후의 빈 초깃값이 그 형태다).
 */
export function isDateRangeValue(value: unknown): value is DateRangeValue {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  // ⚠️ **평범한 객체 리터럴만** 인정한다. 빈 기간 초깃값이 `{}`라 "키가 없어도 통과"가 필요한데,
  // 그 조건만 두면 `new Date()`도 자기 키가 없어 기간으로 오인된다(formatDate가 Date 인스턴스를
  // 정식으로 받으므로 실제로 지나가는 값이다). 프로토타입으로 갈라 그 오인을 원천 차단한다.
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return false;
  const v = value as Record<string, unknown>;
  const okStart = v.start === undefined || typeof v.start === 'string';
  const okEnd = v.end === undefined || typeof v.end === 'string';
  if (!okStart || !okEnd) return false;
  // 무관한 객체(예: annotated-image 값)를 기간으로 오인하지 않도록, 두 키 중 하나는 있어야 한다.
  // 단 빈 객체는 예외로 통과시킨다(빈 기간 초깃값).
  const keys = Object.keys(v);
  if (keys.length === 0) return true;
  return keys.some((k) => k === 'start' || k === 'end');
}

/**
 * 임의 값을 기간 객체로 정규화 — 기간이 아니면 빈 기간. 값이 **문자열**이면 시작일만 있는 기간으로
 * 승격한다(단일 date 필드를 date-range로 바꿨을 때 기존 값을 잃지 않기 위한 하위호환 — 위젯을
 * 바꿨다고 이미 입력된 날짜가 사라지면 안 된다).
 */
export function normalizeDateRange(value: unknown): DateRangeValue {
  if (typeof value === 'string' && value !== '') return { start: value };
  if (!isDateRangeValue(value)) return {};
  return value;
}

/**
 * 기간의 한쪽 끝만 바꾼 새 값 — **반대쪽을 보존해 병합**한다(교체하면 반대쪽이 조용히 지워진다).
 * 빈 문자열로 지운 끝은 키 자체를 없애 `{}`로 수렴시킨다 — 빈 문자열이 남으면 "값이 있다"고 보는
 * 소비처(요약 표시·되돌리기 비교)와 어긋난다.
 *
 * 컨트롤 밖(순수)에 두는 이유: 이 병합 규칙이 곧 기간의 저장 계약이라, DOM 없이 검증돼야 한다.
 */
export function mergeDateRangeEnd(
  value: unknown,
  key: 'start' | 'end',
  next: string,
): DateRangeValue {
  const merged: DateRangeValue = { ...normalizeDateRange(value) };
  if (next === '') delete merged[key];
  else merged[key] = next;
  return merged;
}

/**
 * 기간 표시 문자열 — 각 끝을 `formatEnd`로 접어 ' ~ '로 잇는다(기본 formatEnd=한글 라벨).
 * 한쪽만 있으면 "2026년 3월 5일 ~" / "~ 2026년 3월 7일"처럼 그쪽만 비운다(어느 끝이 열려 있는지가
 * 정보다 — 있는 쪽만 남기면 단일 날짜와 구분되지 않는다). 둘 다 없으면 '' (호출부가 빈값 표기 결정).
 */
export function formatDateRange(
  value: unknown,
  formatEnd: (v: unknown) => string = dateDisplayLabel,
): string {
  const { start, end } = normalizeDateRange(value);
  const s = formatEnd(start);
  const e = formatEnd(end);
  if (s === '' && e === '') return '';
  return `${s} ${DATE_RANGE_SEPARATOR} ${e}`.trim();
}

/** 기간 표시 라벨(한글) — "2026년 3월 5일 ~ 2026년 3월 7일". 값 없음이면 ''. */
export function dateRangeDisplayLabel(value: unknown): string {
  return formatDateRange(value, dateDisplayLabel);
}

/**
 * 날짜/기간 위젯의 **읽기 전용 표시 문자열** — 위젯 어휘를 아는 자리(행 그리드·반복 셀)가
 * 텍스트로 접기 전에 먼저 물어보는 한 지점.
 *
 * 반환 규약:
 *  - `undefined` = 날짜 계열 위젯이 아님 → 호출부의 기존 표기를 그대로 쓴다(회귀 0).
 *  - `''`        = 날짜 계열인데 값이 없음 → 호출부의 빈값 표기('—' 등)를 쓴다.
 *  - 그 외       = 그대로 표시.
 *
 * 빈값 표기를 여기서 정하지 않는 이유: 자리마다 빈 칸 문구가 다르고(그리드 '—' / 셀 '—' /
 * 요약 '(라벨 미입력)'), 그건 날짜의 규약이 아니라 그 화면의 규약이다.
 */
export function dateWidgetDisplayText(widget: string | undefined, value: unknown): string | undefined {
  if (widget === 'date') return dateDisplayLabel(value);
  if (widget === 'date-range') return dateRangeDisplayLabel(value);
  return undefined;
}

/**
 * 기간 raw 표시(yyyy-MM-dd ~ yyyy-MM-dd) — 표시 형식(format) **미지정** 셀의 기본 표기.
 * 단일 date 셀이 format 없이 raw "2026-08-28"로 나오므로 기간도 raw끼리 이어 패리티를 맞춘다
 * (한글 표기는 작성 페이지 readonly와 format 적용 시에만).
 */
export function dateRangeRawText(value: unknown): string {
  return formatDateRange(value, dateInputValue);
}
